import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import "mathlive";
import { MathfieldElement } from "mathlive";
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineCalculate,
    MdOutlineLocationOn,
    MdOutlinePrint,
    MdSuperscript
} from 'react-icons/md';
import { IoMdRadioButtonOn, IoMdRadioButtonOff } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { RiCheckboxFill, RiCheckboxBlankLine } from "react-icons/ri";
import { useReactToPrint } from 'react-to-print';
import Header from 'components/Header';
import Button from 'components/Button2';
import TextInput from 'components/TextInput2';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import DragAndDropBucketPreview from './DragAndDropBucketPreview';
import DragAndDropBlankPreview from './DragAndDropBlankPreview';
import TableGridPReview from './TableGridPreview';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import TableGridPreview from './TableGridPreview';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const DISABLE_PRINT = ['ReadingTown', 'College Planning Corp', 'A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education'];
const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

function TestSHSATPreview() {
    const [testName, setTestName] = useState(null);
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [isPrint, setIsPrint] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [currentDefinition, setCurrentDefinition] = useState(null);
    const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
    const [fullPassage, setFullPassage] = useState<{
        html?: string;
        preview?: string;
    } | null>(null);
    const [printFullPassage, setPrintFullPassage] = useState<Record<string, { html?: string; }>>({});
    const [definitions, setDefinitions] = useState<Array<{
        passageId?: string;
        html?: string;
        preview?: string;
    }>>([]);
    const [questionHelper, setQuestionHelper] = useState(null);
    const [fbQuestionHelper, setFbQuestionHelper] = useState(null);
    const [icQuestionHelper, setIcQuestionHelper] = useState(null);
    // Per-(org, test) clean-print allowlist flag from the preview endpoint.
    const [cleanPrintTest, setCleanPrintTest] = useState(false);

    const { user } = useAuthContext();
    // Clean version applies when the org-wide customization is set OR this specific
    // (org, test) pair is on the per-test clean-print allowlist.
    const cleanVersionEnabled = cleanPrintTest || !!user.customizations.find(customization => customization === 'Test Clean Version Print');
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));
    const mathfieldRef = useRef<MathfieldElement | null>(null);

    useEffect(() => {
        const keyboard = (window as any).mathVirtualKeyboard;
        // keyboard.visible = false;
        // keyboard.hide()

        // always visible
        const questions = testModules?.[moduleIndex]?.questions;
        if (!questions || questions.length === 0) return;
        if (questions?.[questionIndex]?.questionType === 11 || questions?.[questionIndex]?.questionType === 12) {
            mathfieldRef.current.mathVirtualKeyboardPolicy = 'manual'
        } else {
            if (mathfieldRef.current) mathfieldRef.current.mathVirtualKeyboardPolicy = 'auto';
            keyboard.hide();
        }
        
        const mf = mathfieldRef.current;
        if (mf) mf.setAttribute("virtual-keyboard-toolbar", "none");

        if (!keyboard || !mf) return;
        
        if (questions[questionIndex]?.questionType !== 11 && questions[questionIndex]?.questionType !== 12) {
            keyboard.visible = false;
            return;
        }
        
        // keyboard customization
        if (questions[questionIndex]?.questionType === 11) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","x","y",""],
                    ["6","7","8","9","0","+","−","\\sqrt{#0}"],
                    ["\\%","−",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","\\cdot","\\div","\\sqrt[#0]{#0}"],
                    ["","{#0}^{#0}","({#0})","\\le","\\lt","=","\\ge","\\gt"],
                    ["\\pi","","","","","","","\\lvert{#@}\\rvert"]
                ]
            };
            keyboard.show();
            keyboard.visible = true;
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".testSHSATPreview__eeContainer .MLK__keycap");
                let minusCount = 0; 

                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "x": tooltip = "letter x"; break;
                        case "y": tooltip = "letter y"; break;
                        case "+": tooltip = "add"; break;
                        case "−":
                        case "-":
                            minusCount++;
                            tooltip = minusCount === 1 ? "subtract" : "negative sign";
                            break;
                        case "÷": tooltip = "divide"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "=": tooltip = "equal"; break;
                        case "≤": tooltip = "less than or equal"; break;
                        case "<": tooltip = "less than"; break;
                        case "≥": tooltip = "greater than or equal"; break;
                        case ">": tooltip = "greater than"; break;
                        case "π": tooltip = "constant pi"; break;
                        case "⋅": tooltip = "multiply dot"; break;
                        case "√▢​": tooltip = "square root"; break;
                        case "▢√▢​": tooltip = "general root"; break;
                        case "▢▢": tooltip = "exponent"; break;
                        case "(▢)": tooltip = "parentheses"; break;
                        case "∣■∣": tooltip = "absolute value"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);

        } else if (questions[questionIndex]?.questionType === 12) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","","",""],
                    ["6","7","8","9","0","","",""],
                    ["\\%","-",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","","",""]
                ]
            }; 
            keyboard.show();
            keyboard.visible = true;   
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".testSHSATPreview__eeContainer .MLK__keycap");
                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "−": tooltip = "negative sign"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);        
        }

        // remove toggle menu buttons, keyboard location, keyboard styling
        const style = document.createElement("style");
        style.innerHTML = `
            math-field::part(menu-toggle),
            math-field::part(virtual-keyboard-toggle) { display: none !important; }

            .testSHSATPreview__eeContainer { position: relative; }
            .testSHSATPreview__eeContainer .ML__virtual-keyboard {
                position: absolute !important;
                top: calc(100% + 8px) !important;
                left: 0 !important;
                width: 620px !important;
                z-index: 999 !important;
            }
            .testSHSATPreview__eeContainer .MLK__backdrop {
                background: none;
            }
            .testSHSATPreview__eeContainer .MLK__toolbar { display: none; }
            .testSHSATPreview__eeContainer .MLK__keycap {
                border: 1px solid #676767;
                flex: 1;
                cursor: none;
                pointer-events: none;
            }
            .testSHSATPreview__eeContainer .ML__keyboard [data-tooltip] {
                position: relative;
                pointer-events: none;
            }
            .ML__latex .ML__vlist-t2,
            .ML__delim-size1 {
                font-size: 16px;
            }
            .testSHSATPreview__eeContainer .MLK__toolbar { 
                justify-content: flex-start; 
            }
            .testSHSATPreview__eeContainer .MLK__plate {
                border: 1px solid #bdbdbd;
                padding: 4px;
            }
            .testSHSATPreview__eeContainer :where(.MLK__rows > .MLK__row div) {
                border: 1px solid #bdbdbd;
                border-radius: 1px;
                font-size: 18px;
                background: white;
            }
            .testSHSATPreview__eeContainer .MLK__rows > .MLK__row div:empty {
                pointer-events: none;
                opacity: 0.5;         
                background-color: white;
                border: 1px solid #e0e0e0ff;
            }
            .testSHSATPreview__eeContainer .MLK__rows {
                gap: 5px;
            }    
            .testSHSATPreview__eeContainer .MLK__rows > .MLK__row {
                gap: 5px;
                justify-content: flex-start;
            }    
            .testSHSATPreview__eeContainer .MLK__row:nth-child(-n+2) div:nth-child(-n+5) {
                background-color: #eeeeeec7 !important;
            }
        `;
        document.head.appendChild(style);

        // keyboard location
        const container = document.querySelector(".testSHSATPreview__eeContainer");
        let observer: MutationObserver | null = null;
        if (container) {
            keyboard.container = container;
            keyboard.alwaysVisible = true;
            keyboard.floater = false;
            keyboard.autoDismiss = false;
            keyboard.show();
        }
        setTimeout(() => {
            const vk = container?.querySelector(".ML__keyboard") as HTMLElement;
            if (vk) {
                // keyboard location styling
                vk.style.height = "260px";
                vk.style.pointerEvents = "auto";
                vk.style.opacity = "1";
                vk.style.visibility = "visible";
                vk.style.removeProperty('--_keyboard-height'); 
                vk.style.height = '300px';    

                // always keyboard visible
                observer = new MutationObserver(() => {
                    if (
                        vk.style.display === "none" ||
                        vk.style.visibility === "hidden" ||
                        vk.style.opacity === "0"
                    ) {
                        vk.style.display = "block";
                        vk.style.visibility = "visible";
                        vk.style.opacity = "1";
                        vk.style.pointerEvents = "auto";
                    }
                });
                observer.observe(vk, {
                    attributes: true,
                    attributeFilter: ["style"],
                    subtree: false,
                });
            }
            if (mf) {
                // always initially
                mf.setAttribute("readonly", "true"); // 입력 불가
                mf.style.caretColor = "transparent";  // 커서 숨기기
                mf.style.userSelect = "none";         // 텍스트 선택 불가
                mf.style.pointerEvents = "none";      // 클릭 이벤트 차단
                // input focus style
                mf.addEventListener("focusin", () => { mf.style.border = "2px solid #bdbdbd";});
                mf.addEventListener("focusout", () => { mf.style.border = "1px solid #bdbdbd";});
                
                // always keyboard visible
                const preventHide = (e: FocusEvent) => {
                    setTimeout(() => {
                        if (mf && document.activeElement !== mf) { // refocus
                            mf.focus();
                        }
                        keyboard.show();
                        if (vk) {
                            vk.style.display = "block";
                            vk.style.visibility = "visible";
                            vk.style.opacity = "1";
                            vk.style.pointerEvents = "auto";
                        }
                    }, 10); // delay after hiding keyboard
                };
                mf.addEventListener("blur", preventHide, true);
                mf.addEventListener("focusout", preventHide, true);
            }
        }, 50);
        return () => {
            // style 제거
            document.head.removeChild(style);
            // observer 해제
            if (observer) observer.disconnect();
        };
    }, [questionIndex]);

    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;
        let correctAnswer = testModules[moduleIndex].questions[questionIndex].answers.map(ans => ans.replace(/\\\\/g, '\\')).join(', ');

        if (showAnswer) {
            mf.setValue(" \\; \\color{green}{" + correctAnswer + "}");
        } else {
            mf.setValue("");
        }

    }, [showAnswer, questionIndex]);

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate('/tests');
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('testSHSATPreview__passages');
        let question = document.getElementById('testSHSATPreview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    // definition
    const handleDefinition = () => {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        const passages = document.getElementsByClassName('testSHSATPreview__Definition');
        const passage = Array.from(passages).find(passage => passage.contains(anchorNode) && passage.contains(focusNode));
        if (!passage) {
            return;
        }

        let startOffset = 0;
        let endOffset = 0;

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === anchorNode) {
                startOffset += anchorOffset;
                break;
            } 

            else if (childNode.contains(anchorNode)) {
                const childText = childNode.textContent;
                const anchorText = anchorNode.textContent;
                const indexInChild = childText.indexOf(anchorText);
                if(indexInChild === -1) {
                    console.error("anchorText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + anchorOffset;
                startOffset += lengthBeforeAnchor;
                break;
            }
            else {
                startOffset += childLength;
            }
        }

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === focusNode) {
                endOffset += focusOffset;
                break;
            } 
            else if (childNode.contains(focusNode)) {
                const childText = childNode.textContent;
                const focusText = focusNode.textContent;
                const indexInChild = childText.indexOf(focusText);
                if(indexInChild === -1) {
                    console.error("focusText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + focusOffset;
                endOffset += lengthBeforeAnchor;
                break;
            } 
            else {
                endOffset += childLength;
            }
        }

        if (startOffset > endOffset) {
            const temp = startOffset;
            startOffset = endOffset;
            endOffset = temp;
        }

        if (startOffset > endOffset) {
            [startOffset, endOffset] = [endOffset, startOffset];
        }

        // call definition
        const definitions = questions[questionIndex]?.passages?.indices
        definitions.forEach((def) => {
            const {startIndex, endIndex, definition} = def
            if (startOffset >= startIndex && endOffset <= endIndex) {
                setCurrentDefinition({
                    ...def,
                    _updatedAt: Date.now() // 강제로 매번 달라지게 하는 값
                });
            }
        })
    }

    useEffect(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        const popupWidth = 700; 
        const popupHeight = 100;
        const gap = 8;

        // selection 영역 위쪽에 뜨도록
        const top = rect.top - popupHeight - gap;
        const left = (rect.left + rect.right) / 2 - popupWidth / 2 + 160;

        setPopupPos({ top, left });
    }, [currentDefinition]);

    // Definition
    const generateDefinitionLink = ({id: passageId, indices, passageFigures = []}) => {
        for (let j = 0; j < indices.length; j++){
            const passage = document.getElementById(passageId);
            let passageHTML = '';
            let passagePreview = '';
            const usedFigureIndices = new Set();
            const { startIndex, endIndex } = indices[j];
            let startOffset = startIndex
            let endOffset = endIndex
            let lastIndex = 0;

            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];

                const addMarkTags = (node) => {
                    if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        // 이미지 삽입을 위한 passageFigure 검사
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            usedFigureIndices.add(currentFigure.figure.index);
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
                        const { textContent } = node;
    
                        if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark class="testSHSATPreview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                        }
                        else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark class="testSHSATPreview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                        }
                        else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                            passageHTML += '<mark class="testSHSATPreview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(0, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endOffset - lastIndex);
                        }
                        else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                            passageHTML += '<mark class="testSHSATPreview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }
            const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
            if (currentFigure) {
                passageHTML += `
                    <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                `;                    
            }
            
            setDefinitions(prev => [...(prev || []), { 
                passageId: passageId,
                html: passageHTML, 
                preview: passagePreview 
            }]);
        }
    };

    const getPassageWithFigure = ({id: passageId, passageFigures = []}) => {
        const passage = document.getElementById(passageId);
        let passageHTML = '';
        let lastIndex = 0;
        const usedFigureIndices = new Set();

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];
            // add open tags first while see the text
            const addMarkTags = (node) => {
                if (node.outerHTML) {
                    const { innerHTML, outerHTML } = node;
                    const childOffset = outerHTML.indexOf('>') + 1;

                    passageHTML += outerHTML.slice(0, childOffset);

                    for (let i = 0; i < node.childNodes.length; i++) {
                        const child = node.childNodes[i];
                        addMarkTags(child);
                    }

                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                } 
                else {
                    // 이미지 삽입을 위한 passageFigure 검사
                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                    if (currentFigure) {
                        usedFigureIndices.add(currentFigure.figure.index);
                        passageHTML += `
                            <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                        `;                    
                    }
                    const { textContent } = node;
                    passageHTML += textContent;
                    lastIndex += textContent.length;
                }  
            }

            addMarkTags(childNode);
        }

        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
        if (currentFigure) {
            passageHTML += `
                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
            `;                    
        }

        setFullPassage({
            html: passageHTML
        });

    };

    const getArrayPassageWFigure = () => {
        let newPassages = {}; // 새로운 `printFullPassage` 데이터를 저장할 객체
    
        for (let i = 0; i < testModules.length; i++) {
            const module = testModules[i];
    
            for (let j = 0; j < module.questions.length; j++) {
                const question = module.questions[j];
                if (question.passages) {
                    const id = question.passages.id;
                    const passageFigures = question.passages.passageFigures || [];
                    if (!newPassages[id]) {
                        const passage = document.getElementById(`print ${id}`);
                        let passageHTML = '';
                        let lastIndex = 0;
                
                        const usedFigureIndices = new Set();

                        for (let i = 0; i < passage.childNodes.length; i++) {
                            const childNode = passage.childNodes[i];
                            // add open tags first while see the text
                            const addMarkTags = (node) => {
                                if (node.outerHTML) {
                                    const { innerHTML, outerHTML } = node;
                                    const childOffset = outerHTML.indexOf('>') + 1;
                
                                    passageHTML += outerHTML.slice(0, childOffset);
                
                                    for (let i = 0; i < node.childNodes.length; i++) {
                                        const child = node.childNodes[i];
                                        addMarkTags(child);
                                    }
                
                                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                                } 
                                else {
                                    // 이미지 삽입을 위한 passageFigure 검사
                                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                                    if (currentFigure) {
                                        usedFigureIndices.add(currentFigure.index);
                                        passageHTML += `
                                            <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                                        `;
                                    }
                                    const { textContent } = node;
                                    passageHTML += textContent;
                                    lastIndex += textContent.length;
                                }  
                            }
                
                            addMarkTags(childNode);
                        }
                
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
            
                        newPassages[id] = {
                            html: passageHTML
                        };
                    }
                }
            }
        }
        setPrintFullPassage(newPassages); // 최종 업데이트
        return Object.keys(newPassages).length > 0;
    };

    // Question Helper
    const generateQuestionHelper = ({ questionHelper, hotText = [], correctAnswers = [] }) => {
        if (!questionHelper) return '';

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;
        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const className = showAnswer && correctAnswers.includes(index)? " correct": "";
            return `<div class="testSHSATPreview__questionHelperHotText${className}">${match}</div>`;
        });
        setQuestionHelper(html);
    };

    // Fill in the Blank Question Helper
    const generateFBQuestionHelper = ({ questionHelper, correctAnswers = []}) => {
        if (!questionHelper) return '';
        const groupedCorrects = {};
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrects[idx]) groupedCorrects[idx] = [];
            groupedCorrects[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const correctVals = groupedCorrects[index] || []; 
            const correctAnswer = correctVals.length > 0 ? correctVals.join(", ") : "";
            // const value = showAnswer? 
            const width = Math.max(50, correctVals.reduce((acc, ans) => Math.max(acc, ans?.length || 0), 0) * 20 + 30)
            if (showAnswer) {
                return `
                    <span class="testSHSATPreview__questionHelperFBBox" style="display:inline-block; width:${width}px">
                        <span class="testSHSATPreview__fbCorrectAnswer">${correctAnswer}</span>
                    </span>
                `;
            } else {
                return `<input class="testSHSATPreview__questionHelperFB" style="width:${width}px" data-index="${index}" value="" />`;
            }        
        });
        setFbQuestionHelper(html);
    };

    // Inline Choice Question Helper
    const generateICQuestionHelper = ({ questionHelper, inlineChoices = [], correctAnswers = [] }) => {
        if (!questionHelper) return '';
        // index별로 답을 묶어줌
        const groupedCorrect = {};
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrect[idx]) groupedCorrect[idx] = [];
            groupedCorrect[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;

            // index별 inlineChoices 찾아오기
            const choices = inlineChoices.filter(choice => choice.index === index);
            const width = Math.max(70, (choices.length > 0 ? Math.max(...choices.map(c => c.answer?.length || 0)) : 0) * 20);
            const optionsHtml = choices
                .map(
                    (choice) => {
                        const correctAnswer = groupedCorrect[index]?.includes(choice.answer);
                        let className = ''
                        if (showAnswer && correctAnswer) {
                            className = "testSHSATPreview__fbCorrectAnswer";
                        } 
                        return `<span class="${className}" value="${choice.answer}">${choice.answer}</span>`
                })
                .join(", ");

            return `<span class="testSHSATPreview__questionHelperIC" style="width:${width}px" data-index="${index}">
                        ${optionsHtml}
                    </span>`;
        });
        setIcQuestionHelper(html);
    };

    // Pure (stateless) helper-HTML generators for print mode.
    // The print div renders ALL questions at once, so we can't rely on the
    // single-question helper state above. These return the HTML string for a
    // given question so each question can be rendered inline.
    const generateQuestionHelperHTML = (question, showAns) => {
        const helper = question.questionHelper;
        if (!helper) return '';
        const correctAnswers = question.hotText?.filter(ht => ht.isCorrect).map(ht => ht.index) || [];
        let html = helper.questionHelper;
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const className = showAns && correctAnswers.includes(index) ? " correct" : "";
            return `<div class="testSHSATPreview__questionHelperHotText${className}">${match}</div>`;
        });
        return html;
    };

    const generateFBQuestionHelperHTML = (question, showAns) => {
        const helper = question.questionHelper;
        if (!helper) return '';
        const correctAnswers = question.answers || [];
        const groupedCorrects = {};
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrects[idx]) groupedCorrects[idx] = [];
            groupedCorrects[idx].push(val);
        });
        let html = helper.questionHelper;
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const correctVals = groupedCorrects[index] || [];
            const correctAnswer = correctVals.length > 0 ? correctVals.join(", ") : "";
            const width = Math.max(50, correctVals.reduce((acc, ans) => Math.max(acc, ans?.length || 0), 0) * 20 + 30);
            if (showAns) {
                return `
                    <span class="testSHSATPreview__questionHelperFBBox" style="display:inline-block; width:${width}px">
                        <span class="testSHSATPreview__fbCorrectAnswer">${correctAnswer}</span>
                    </span>
                `;
            }
            return `<input class="testSHSATPreview__questionHelperFB" style="width:${width}px" data-index="${index}" value="" />`;
        });
        return html;
    };

    const generateICQuestionHelperHTML = (question, showAns) => {
        const helper = question.questionHelper;
        if (!helper) return '';
        const correctAnswers = question.answers || [];
        const inlineChoices = question.inlineChoices || [];
        const groupedCorrect = {};
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrect[idx]) groupedCorrect[idx] = [];
            groupedCorrect[idx].push(val);
        });
        let html = helper.questionHelper;
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const choices = inlineChoices.filter(choice => choice.index === index);
            const width = Math.max(70, (choices.length > 0 ? Math.max(...choices.map(c => c.answer?.length || 0)) : 0) * 20);
            const optionsHtml = choices
                .map((choice) => {
                    const correctAnswer = groupedCorrect[index]?.includes(choice.answer);
                    const className = showAns && correctAnswer ? "testSHSATPreview__fbCorrectAnswer" : "";
                    return `<span class="${className}" value="${choice.answer}">${choice.answer}</span>`;
                })
                .join(", ");
            return `<span class="testSHSATPreview__questionHelperIC" style="width:${width}px" data-index="${index}">
                        ${optionsHtml}
                    </span>`;
        });
        return html;
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        applyKatexWrap(document, {
            // Optional-chained: the old loop only dereferenced this once it had
            // at least one base to act on, so it never ran on the render where
            // testModules is still null. Hoisting it into an argument made it
            // eager, which crashed.
            enabled: testModules?.[moduleIndex]?.subject === 'English Language Arts',
        });

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const dragMove = (element: HTMLElement, body: HTMLElement, parent: HTMLElement, padding: number = 0) => {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        const dragMouseDown = (event) => {
            event.preventDefault();
            // if (element.id!=='magnifierDrag') {
            //     const element2 = document.getElementById(`${element.id}2`);
            //     const allElements = document.querySelectorAll('.draggable-element'); // 드래그 가능한 모든 요소들
            //     allElements.forEach((el) => {
            //         const draggableElement = el as HTMLElement;
            //         draggableElement.style.zIndex = '2';
            //     });
            //     element.style.zIndex = '3'
            //     element2.style.zIndex = '3'
            // }
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;

            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            // 🔹 부모 요소의 크기 가져오기
            const parentRect = parent.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // 🔹 새로운 위치 계산 (부모 요소 안에 머물도록 제한)
            let newTop = element.offsetTop - pos2;
            let newLeft = element.offsetLeft - pos1;

            newTop = Math.max(padding, Math.min(newTop, parentRect.height - elementRect.height - padding));
            newLeft = Math.max(padding, Math.min(newLeft, parentRect.width - elementRect.width - padding));

            //🔹 새로운 위치 적용
            element.style.top = `${newTop}px`;
            element.style.left = `${newLeft}px`;
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
        

        body.onmousedown = (event) => {
            dragMouseDown(event);
        };
    
        body.ontouchstart = (event) => {
            dragMouseDown(event);
        };
    };

    const printLog = () => {
        axios.post('tutors/tests/printLog/', {
                'testId': testId,
                'cleanVersion': (!showAnswer && cleanVersionEnabled) ? true : false
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => {
            setIsPrint(false);
            printLog();
        }
    });

    const onPrint = () =>{
        setIsPrint(true);
    }

    useEffect(()=>{
        if(isPrint) {
            renderKatex()
            const hasPassages = getArrayPassageWFigure()
            // Tests with no passages never update printFullPassage, so the
            // effect below won't fire — print directly in that case.
            if (!hasPassages) {
                handlePrint();
            }
        }
    }, [isPrint]);

    useEffect(() => {
        if (Object.keys(printFullPassage).length > 0) {
            handlePrint();
        }
    }, [printFullPassage]);

    useEffect(() => {
        axios.get(`tests/digital/shsat/preview/${testId}/`)
            .then(res => {
                setTestName(res.data.name)
                setTestModules(res.data.testModules);
                setCleanPrintTest(res.data.cleanPrintTest);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(()=>{
        // definition move
        const definitionDragEl = document.getElementById('definitionDrag');
        const definitionHeaderEl = document.getElementById('definitionHeader');
        const definitionParentEl = document.getElementById('testSHSATPreview');
        if (definitionDragEl && definitionHeaderEl && definitionParentEl) {
            dragMove(definitionDragEl, definitionHeaderEl, definitionParentEl);
        }
    }, [currentDefinition])

    useEffect(()=>{
        // explanation move
        const explanationDragEl = document.getElementById('explanationDrag');
        const explanationHeaderEl = document.getElementById('explanationHeader');
        const explanationParentEl = document.getElementById('testSHSATPreview');
        if (explanationDragEl && explanationHeaderEl && explanationParentEl) {
            dragMove(explanationDragEl, explanationHeaderEl, explanationParentEl);
        }
    }, [showExplanation])

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            if (testModules[moduleIndex].questions[questionIndex].passages) {
                setDefinitions([]);
                setFullPassage(null);
                const passageData = testModules[moduleIndex].questions[questionIndex]?.passages;
                if (passageData) {
                    const { id, indices, passageFigures } = testModules[moduleIndex].questions[questionIndex].passages
                    if (indices) generateDefinitionLink({ id, indices, passageFigures });  
                    getPassageWithFigure({ id, passageFigures });
                }
            }
            // question helper
            if (testModules[moduleIndex].questions[questionIndex]?.questionHelper) {
                if (testModules[moduleIndex].questions[questionIndex]?.questionType === 4) {
                    generateQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        hotText: testModules[moduleIndex].questions[questionIndex]?.hotText || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.hotText?.filter(ht => ht.isCorrect).map(ht => ht.index) || []
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 7) {
                    generateFBQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || []
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 9) {
                    generateICQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        inlineChoices: testModules[moduleIndex].questions[questionIndex]?.inlineChoices || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || []
                    });
                }
            } 
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen]);

    useEffect(()=>{
        renderKatex();
    }, [questionHelper, fbQuestionHelper, icQuestionHelper]);

    useEffect(()=>{
        // question helper
        if (testModules) {
            if (testModules[moduleIndex].questions[questionIndex]?.questionHelper) {
                if (testModules[moduleIndex].questions[questionIndex]?.questionType === 4) {
                    generateQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        hotText: testModules[moduleIndex].questions[questionIndex]?.hotText || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.hotText?.filter(ht => ht.isCorrect).map(ht => ht.index) || []                
                    }); 
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 7) {
                    generateFBQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || []
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 9) {
                    generateICQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        inlineChoices: testModules[moduleIndex].questions[questionIndex]?.inlineChoices || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || []   
                    });
                }
            } 
        }
    }, [showAnswer]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        subject,
        prompt
    } = testModules[moduleIndex];

    // Tab/title label: adaptive modules carry a Medium/Hard tier and show as
    // "English Language Arts — Medium" etc.; legacy modules show the subject.
    const moduleLabel = (m: any) => (m?.tier ? `${m.subject} — ${m.tier}` : m?.subject);

    // In print mode, answers are shown when "Show answer" is toggled on OR clean
    // version is not enabled (org-wide customization or per-test allowlist).
    const printShowAnswer = showAnswer || !cleanVersionEnabled;

    return (
        <>
        <Helmet> 
            <title>
                Test Preview - Test Center Admin
            </title>
        </Helmet>
        <Header title={testName} prevRoutes={[{ name: 'All Tests', path: '/tests' }]}/>
        <div className='testSHSATPreview__box' onCopy={(event) => { event.preventDefault() }}>
            {/* screen that will be appear when click button is working */}
            {isPrint &&
                <div className='testSHSATPreview__print' ref={printRef}>
                    {testModules.map((testModule, m) => (<>
                        <h2 className={m > 0
                            ? 'testSHSATPreview__title testSHSATPreview__pageBreakBefore'
                            : 'testSHSATPreview__title'
                        }>
                            {testModule.subject.replace('ACT ', '')}
                        </h2>
                        {testModule.questions.map((question, q) => {
                            return (
                                <div className='testSHSATPreview__test testSHSATPreview__pageBreak'>
                                    {/* {question.passages && */}
                                    {(question.passages && testModule.questions.findIndex(moduleQuestion => moduleQuestion.passages?.id === question.passages.id) === q) && 
                                    <>
                                        <div className='testSHSATPreview__materialContent'>
                                            <div key={question.passages.id} className='testSHSATPreview__passage'>
                                                <p
                                                    id={`print ${question.passages.id}`}
                                                    className='katex testSHSATPreview__passageText'
                                                >
                                                    {question.passages.passage}
                                                </p>
                                                {printFullPassage[question.passages.id] && <p
                                                    id={`passage ${question.passages.id}`}
                                                    className='katex testSHSATPreview__passageTextPassage'
                                                    dangerouslySetInnerHTML={{ __html: printFullPassage[question.passages.id].html || 'yes' }}
                                                />}
                                            </div>
                                        </div>
                                    </>}
                                    <div className='testSHSATPreview__question'>
                                        <div className='testSHSATPreview__questionContent'>
                                            <div className='testSHSATPreview__questionHeader'>
                                                <p className="testSHSATPreview__questionNumber">
                                                    {question.questionNum}
                                                </p>
                                            </div>

                                            <div id='testSHSATPreview__questionChoices' className='testSHSATPreview__questionChoices'>
                                                {question.figures.map(figure => (
                                                    <img
                                                        className='testSHSATPreview__figure'
                                                        key={figure.id}
                                                        src={figure.image}
                                                        alt={(question.questionNum).toString()}
                                                    />
                                                ))}
                                                {/* Skip the raw stem when it carries blank markers
                                                    (``[[ ]]``); the helper re-renders it with inputs.
                                                    Mirrors the student AdaptiveModule guard. */}
                                                {question.question && !question.question.includes('[[') &&
                                                    <p className='testSHSATPreview__questionQuestion'>
                                                        <span className='katex'>
                                                            {question.question}
                                                        </span>
                                                    </p>
                                                }
                                                {question.questionType===1
                                                ? <>
                                                        {question.choices.map((choice, i) => (
                                                            <div key={choice.id}
                                                                className='testSHSATPreview__questionChoiceWrapper'
                                                            >
                                                                <div
                                                                    className='testSHSATPreview__choiceContainer'
                                                                >
                                                                    <div
                                                                        className={(choice.isCorrect && printShowAnswer)
                                                                            ? 'testSHSATPreview__radioButton--selected'
                                                                            : 'testSHSATPreview__radioButton'
                                                                        }>
                                                                            {(choice.isCorrect && printShowAnswer)?
                                                                                <IoMdRadioButtonOn className='testSHSATPreview__radioButtonIcon'/>
                                                                                : <IoMdRadioButtonOff className='testSHSATPreview__radioButtonIcon'/>
                                                                            }
                                                                    </div>
                                                                    <div className={(choice.isCorrect && printShowAnswer)
                                                                        ? 'testSHSATPreview__questionChoiceContent--selected'
                                                                        : 'testSHSATPreview__questionChoiceContent'
                                                                    }>
                                                                        <p className='testSHSATPreview__choiceText'>
                                                                            {MULTIPLE_CHOICES[i]}.
                                                                        </p>
                                                                        {choice.figure
                                                                        ?   <img
                                                                                className='testSHSATPreview__questionChoiceFigure'
                                                                                key={choice.figure}
                                                                                src={choice.figure}
                                                                                alt={choice.figure}
                                                                            />
                                                                        :   <p className='testSHSATPreview__questionChoiceText'>
                                                                                <span className='katex'>
                                                                                    {choice.choice}
                                                                                </span>
                                                                            </p>
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                : question.questionType===3
                                                ? <>
                                                    {question.choices.map((choice, i) => (
                                                        <div key={choice.id}
                                                            className='testSHSATPreview__questionChoiceWrapper'
                                                        >
                                                            <div
                                                                className='testSHSATPreview__choiceContainer'
                                                            >
                                                                <div
                                                                    className={(choice.isCorrect && printShowAnswer)
                                                                        ? 'testSHSATPreview__radioButton--selected'
                                                                        : 'testSHSATPreview__radioButton'
                                                                    }>
                                                                        {(choice.isCorrect && printShowAnswer)?
                                                                        <RiCheckboxFill className='testSHSATPreview__radioButtonIcon'/>
                                                                        : <RiCheckboxBlankLine className='testSHSATPreview__radioButtonIcon'/>
                                                                        }
                                                                </div>
                                                                <div className={(choice.isCorrect && printShowAnswer)
                                                                    ? 'testSHSATPreview__questionChoiceContent--selected'
                                                                    : 'testSHSATPreview__questionChoiceContent'
                                                                }>
                                                                    <p className='testSHSATPreview__choiceText'>
                                                                        {MULTIPLE_CHOICES[i]}.
                                                                    </p>
                                                                    {choice.figure
                                                                    ?   <img
                                                                            className='testSHSATPreview__questionChoiceFigure'
                                                                            key={choice.figure}
                                                                            src={choice.figure}
                                                                            alt={choice.figure}
                                                                        />
                                                                    :   <p className='testSHSATPreview__questionChoiceText'>
                                                                            <span className='katex'>
                                                                                {choice.choice}
                                                                            </span>
                                                                        </p>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                                : question.questionType===4
                                                ? <div className='testSHSATPreview__questionChoiceWrapper'>
                                                    <p
                                                        className='katex testSHSATPreview__questionHelperText'
                                                        dangerouslySetInnerHTML={{ __html: generateQuestionHelperHTML(question, printShowAnswer) }}
                                                    ></p>
                                                </div>
                                                : question.questionType===5
                                                ? <div>
                                                    <DragAndDropBucketPreview
                                                        question={question}
                                                        showAnswer={printShowAnswer}
                                                    />
                                                </div>
                                                : question.questionType===6
                                                ? <div>
                                                    <DragAndDropBlankPreview
                                                        question={question}
                                                        showAnswer={printShowAnswer}
                                                    />
                                                </div>
                                                : question.questionType===7
                                                ? <div className='testSHSATPreview__questionContainer'>
                                                    <div className='testSHSATPreview__questionChoiceWrapper'>
                                                        <p
                                                            className='katex testSHSATPreview__questionHelperText'
                                                            dangerouslySetInnerHTML={{ __html: generateFBQuestionHelperHTML(question, printShowAnswer) }}
                                                        ></p>
                                                    </div>
                                                </div>
                                                : question.questionType===8
                                                ? <div className='testSHSATPreview__questionContainer'>
                                                    <TableGridPreview
                                                        question={question}
                                                        showAnswer={printShowAnswer}
                                                    />
                                                </div>
                                                : question.questionType===9
                                                ? <div className='testSHSATPreview__questionContainer'>
                                                    <div className='testSHSATPreview__questionChoiceWrapper'>
                                                        <p
                                                            className='katex testSHSATPreview__questionHelperText'
                                                            dangerouslySetInnerHTML={{ __html: generateICQuestionHelperHTML(question, printShowAnswer) }}
                                                        ></p>
                                                    </div>
                                                </div>
                                                : question.questionType===10
                                                ? <div className='testSHSATPreview__questionContainerHotSpot'>
                                                    {question.choices?.map((choice) => (
                                                        <div
                                                            key={choice.id}
                                                            className={printShowAnswer && choice.isCorrect
                                                                ? 'testSHSATPreview__choiceContainerHotSpot--correct'
                                                                : 'testSHSATPreview__choiceContainerHotSpot'
                                                            }
                                                        >
                                                            {choice.figure
                                                            ?   <img
                                                                    className='testSHSATPreview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                    draggable="false"
                                                                />
                                                            :   <p className='testSHSATPreview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </div>
                                                    ))}
                                                </div>
                                                : (question.questionType===11 || question.questionType===12)
                                                ? <div className='testSHSATPreview__questionContainer'>
                                                    {printShowAnswer &&
                                                        <p className='testSHSATPreview__eeAnswerPrint'>
                                                            <span className='katex'>
                                                                {`\\(${question.answers.map(ans => ans.replace(/\\\\/g, '\\')).join(',\\; ')}\\)`}
                                                            </span>
                                                        </p>
                                                    }
                                                </div>
                                                : <></>
                                                }
                                                {printShowAnswer && <>
                                                    <div>Explanation: </div>
                                                    {question.explanationFigure && (
                                                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                                            <img
                                                                src={question.explanationFigure}
                                                                alt="Explanation Figure"
                                                                style={{ maxWidth: question.explanation ? '70%' : '100%', height: 'auto' }}
                                                            />
                                                        </div>
                                                    )}
                                                    {question.explanation &&
                                                        <div className='testSHSATPreview__printExpl'>
                                                            <span className='katex'>{question.explanation}</span>
                                                        </div>
                                                    }
                                                </>}
                                                <div className='testSHSATPreview__marginBottom'></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </>))}
                </div>
            }

            <div id='testSHSATPreview' className='testSHSATPreview'>
                <div id='definitionDrag' className={currentDefinition
                    ? 'testSHSATPreview__popup--definition'
                    : 'testSHSATPreview__popup--hidden'
                }
                    style={{
                        position: 'absolute',
                        top: popupPos.top,
                        left: popupPos.left
                    }}
                >
                    <div id='definitionHeader' className='testSHSATPreview__popupHeader draggable-element'>
                        <h3 className='testSHSATPreview__popupHeaderText'>Glossary Definition</h3>
                        <IoClose className='testSHSATPreview__popupHeaderCloseBig' onClick={()=> setCurrentDefinition(null)} />
                    </div>
                    <div className='testSHSATPreview__popupDefinition'>
                        {currentDefinition?.definition}
                    </div>
                </div>
                {/* test Header */}
                <div className='testSHSATPreview__header'>
                    <div className='testSHSATPreview__titleGroup'>
                        <button className='testSHSATPreview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='testSHSATPreview__title'>
                                {moduleLabel(testModules[moduleIndex])}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='testSHSATPreview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='testSHSATPreview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='testSHSATPreview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'testSHSATPreview__moduleSelectDropdownItem--selected'
                                                : 'testSHSATPreview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {moduleLabel(testModule)}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='testSHSATPreview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='testSHSATPreview__directionsIcon' />
                                : <MdKeyboardArrowDown className='testSHSATPreview__directionsIcon' />
                            }
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='testSHSATPreview__directions' ref={directionsRef}>
                            <div className='testSHSATPreview__directionsBubble'>
                                <p className='katex testSHSATPreview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='testSHSATPreview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }

                    {/* header action */}
                    <div className='testSHSATPreview__actionGroup'>
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) &&
                            <button
                                className='testSHSATPreview__action'
                                onClick={onPrint}
                            >
                                <MdOutlinePrint className='testSHSATPreview__actionIcon' />
                                <p className='testSHSATPreview__actionText'>Print</p>
                            </button>
                        }
                        <button
                            className='testSHSATPreview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='testSHSATPreview__actionIcon' />
                            <p className='testSHSATPreview__actionText'>Exit</p>
                        </button>
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'testSHSATPreview__popup--explanation'
                        : 'testSHSATPreview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='testSHSATPreview__popupHeader draggable-element'>
                            <h3 className='testSHSATPreview__popupHeaderText'>Explanation</h3>
                            <IoClose className='testSHSATPreview__popupHeaderCloseBig' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='testSHSATPreview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt="Explanation Figure"
                                        style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                    />
                                </div>
                            )}
                            <div className='testSHSATPreview__explanationBox katex'>
                                {questions[questionIndex].explanation
                                    ? questions[questionIndex].explanation
                                    : questions[questionIndex].explanationFigure
                                    ? ''
                                    : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testSHSATPreview__dottedLine'></div>


                {/* test content */}
                <div className='testSHSATPreview__test'>
                    {/* left-passage */}
                    {questions[questionIndex].passages && <>
                        <div className='testSHSATPreview__material'>
                            <div className='testSHSATPreview__materialContent'>
                                <div key={questions[questionIndex].passages.id} className='testSHSATPreview__passage'>
                                    {/* 해당 id를 가진 passage의 p태그 가져와서 highlight 넣은 html 받아와서 겹쳐 보여줌 (z-index, transparent 조절) */}
                                    <p
                                        id={questions[questionIndex].passages.id}
                                        className='katex testSHSATPreview__passageText'
                                    >
                                        {questions[questionIndex].passages.passage}
                                    </p>
                                    <p
                                        id={`passage ${questions[questionIndex].passages.id}`}
                                        className='katex testSHSATPreview__passageTextPassage'
                                        dangerouslySetInnerHTML={{ __html: fullPassage?.html }}
                                    >
                                    </p>
                                    {/* glossary definition */}
                                    {
                                        definitions.map((definition, index)=>
                                            <div
                                                id={`definition ${questions[questionIndex].passages.id}`}
                                                key={index}
                                                className='katex testSHSATPreview__Definition'
                                                onMouseUp={handleDefinition}    
                                                onTouchEnd={handleDefinition}  
                                                dangerouslySetInnerHTML={{ __html: definition?.html }}
                                            ></div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='testSHSATPreview__verticalLine'></div>
                    </>}
                    
                    {/* right-question */}
                    <div className='testSHSATPreview__question'>
                        <div className='testSHSATPreview__questionContent'>
                            {/* header */}
                            <div className='testSHSATPreview__questionHeader'>
                                <p className="testSHSATPreview__questionNumber">
                                    {questions[questionIndex].questionNum}
                                </p>
                                {(questions[questionIndex].explanation || questions[questionIndex].explanationFigure) &&
                                    <button
                                        className={showExplanation
                                            ? 'testSHSATPreview__explanation--active'
                                            : 'testSHSATPreview__explanation'
                                        }
                                        onClick={() => setShowExplanation(!showExplanation)}
                                    >
                                        Explanation
                                    </button>
                                }
                                <button
                                    className={showAnswer
                                        ? 'testSHSATPreview__showAnswer--active'
                                        : 'testSHSATPreview__showAnswer'
                                    }
                                    onClick={() => setShowAnswer(!showAnswer)}
                                >
                                    {showAnswer ? 'Hide answer' : 'Show answer'}
                                </button>
                            </div>

                            <div id='testSHSATPreview__questionChoices' className='testSHSATPreview__questionChoices'>
                                {/* question content */}
                                <p className='testSHSATPreview__questionQuestionContainer'>
                                    {/* Skip the raw stem when it carries blank markers
                                        (``[[ ]]``); the helper re-renders it with inputs.
                                        Mirrors the student AdaptiveModule guard. */}
                                    {questions[questionIndex].question && !questions[questionIndex].question.includes('[[') &&
                                        <span className='katex testSHSATPreview__questionQuestion'>
                                            {questions[questionIndex].question}
                                        </span>
                                    }
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='testSHSATPreview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questions[questionIndex].questionNum).toString()}
                                        />
                                    ))}
                                </p>
                                {questions[questionIndex].questionType===1
                                ? <>
                                    {questions[questionIndex].choices.length &&   
                                        <>
                                            {questions[questionIndex].choices.map((choice, i) => (
                                                <div key={choice.id}  
                                                    className='testSHSATPreview__questionChoiceWrapper'
                                                >
                                                    <div 
                                                        className='testSHSATPreview__choiceContainer'
                                                    >
                                                        <div
                                                            className={showAnswer && choice.isCorrect
                                                                ? 'testSHSATPreview__radioButton--selected'
                                                                : 'testSHSATPreview__radioButton'
                                                            }>
                                                                {showAnswer && choice.isCorrect?
                                                                    <IoMdRadioButtonOn className='testSHSATPreview__radioButtonIcon'/>
                                                                    : <IoMdRadioButtonOff className='testSHSATPreview__radioButtonIcon'/>
                                                                }
                                                        </div>
                                                        <div className={showAnswer && choice.isCorrect
                                                            ? 'testSHSATPreview__questionChoiceContent--selected'
                                                            : 'testSHSATPreview__questionChoiceContent'
                                                        }>
                                                            <p className='testSHSATPreview__choiceText'>
                                                                {MULTIPLE_CHOICES[i]}.
                                                            </p>
                                                            {choice.figure
                                                            ?   <img
                                                                    className='testSHSATPreview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                />
                                                            :   <p className='testSHSATPreview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    }
                                </>
                                : questions[questionIndex].questionType===3
                                ? <>
                                    {questions[questionIndex].choices.length &&   
                                        <>
                                            {questions[questionIndex].choices.map((choice, i) => (
                                                <div key={choice.id}  
                                                    className='testSHSATPreview__questionChoiceWrapper'
                                                >
                                                    <div 
                                                        className='testSHSATPreview__choiceContainer'
                                                    >
                                                        <div
                                                            className={showAnswer && choice.isCorrect
                                                                ? 'testSHSATPreview__radioButton--selected'
                                                                : 'testSHSATPreview__radioButton'
                                                            }>
                                                                {showAnswer && choice.isCorrect?
                                                                    <RiCheckboxFill className='testSHSATPreview__radioButtonIcon'/>
                                                                    : <RiCheckboxBlankLine className='testSHSATPreview__radioButtonIcon'/>
                                                                }
                                                        </div>
                                                        <div className={showAnswer && choice.isCorrect
                                                            ? 'testSHSATPreview__questionChoiceContent--selected'
                                                            : 'testSHSATPreview__questionChoiceContent'
                                                        }>
                                                            <p className='testSHSATPreview__choiceText'>
                                                                {MULTIPLE_CHOICES[i]}.
                                                            </p>
                                                            {choice.figure
                                                            ?   <img
                                                                    className='testSHSATPreview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                />
                                                            :   <p className='testSHSATPreview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    }
                                </>
                                : questions[questionIndex].questionType===4
                                ? <>
                                    {questions[questionIndex]?.hotText?.length > 0 && 
                                        <div id='testSHSATPreview__questionHelper' className='testSHSATPreview__questionChoiceWrapper'>
                                            <p
                                                className='katex testSHSATPreview__questionHelperText'
                                                dangerouslySetInnerHTML={{ __html: questionHelper }}
                                            ></p>
                                        </div>
                                    }
                                </>
                                : questions[questionIndex].questionType===5
                                ? <div>
                                    <DragAndDropBucketPreview
                                        question={questions[questionIndex]}
                                        showAnswer={showAnswer}
                                    />
                                </div>
                                : questions[questionIndex].questionType===6
                                ? <div>
                                    <DragAndDropBlankPreview
                                        question={questions[questionIndex]}
                                        showAnswer={showAnswer}
                                    />
                                </div>
                                : questions[questionIndex].questionType===7
                                ? <div className='testSHSATPreview__questionContainer'>
                                    <div className='test tSHSATPreview__questionChoiceWrapper'>
                                        <p
                                            className='katex testSHSATPreview__questionHelperText'
                                            dangerouslySetInnerHTML={{ __html: fbQuestionHelper }}
                                        ></p>
                                    </div>
                                </div>
                                : questions[questionIndex].questionType===8
                                ? <div className='testSHSATPreview__questionContainer'>
                                    <TableGridPreview
                                        question={questions[questionIndex]}
                                        showAnswer={showAnswer}
                                    />
                                </div>
                                : questions[questionIndex].questionType===9
                                ? <div className='testSHSATPreview__questionContainer'>
                                    <div className='testSHSATPreview__questionChoiceWrapper'>
                                        <p
                                            className='katex testSHSATPreview__questionHelperText'
                                            dangerouslySetInnerHTML={{ __html: icQuestionHelper }}
                                        ></p>
                                    </div>
                                </div>
                                : questions[questionIndex].questionType===10
                                ? <div className='testSHSATPreview__questionContainerHotSpot'>
                                    {questions[questionIndex].choices.length &&   
                                        <>
                                            {questions[questionIndex].choices?.map((choice, i) => (
                                                <div 
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'testSHSATPreview__choiceContainerHotSpot--correct'
                                                        : 'testSHSATPreview__choiceContainerHotSpot'
                                                    }
                                                >
                                                    {choice.figure
                                                    ?   <img
                                                            className='testSHSATPreview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure} 
                                                            alt={choice.figure}
                                                            draggable="false"
                                                        />
                                                    :   <p className='testSHSATPreview__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </div>
                                            ))}
                                        </>
                                    }
                                </div>
                                : questions[questionIndex].questionType===11 || questions[questionIndex].questionType===12
                                ? <div className='testSHSATPreview__questionContainer'>
                                    <div className={`testSHSATPreview__eeContainer${navigatorOpen ? ' testSHSATPreview__eeContainer--navOpen' : ''}`}>
                                        <math-field
                                            ref={mathfieldRef}
                                            style={{
                                                width: "620px",
                                                height: "50px",
                                                fontSize: "20px"
                                            }}
                                            className='testSHSATPreview__eeInput'
                                            virtual-keyboard-mode="manual"
                                        >
                                        </math-field>
                                    </div>
                                </div>
                                : <></>
                                }
                                <div className='testSHSATPreview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testSHSATPreview__dottedLine'></div>
                
                {/* text content bottom */}
                <div className='testSHSATPreview__footer'>
                    <button
                        className='testSHSATPreview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='testSHSATPreview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='testSHSATPreview__navigatorToggleIcon' />
                        }
                    </button>

                    <div className='testSHSATPreview__buttons'>
                        <Button
                            className='testSHSATPreview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='testSHSATPreview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>

                    {/* bottom 'Question' button opened */}
                    {navigatorOpen &&
                        <div className='testSHSATPreview__navigator' ref={navigatorRef}>
                            <div className='testSHSATPreview__navigatorBubble'>
                                <h2 className='testSHSATPreview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='testSHSATPreview__navigatorLegends'>
                                    <div className='testSHSATPreview__navigatorLegend'>
                                        <MdOutlineLocationOn className='testSHSATPreview__navigatorLegendCurrent' />
                                        <p className='testSHSATPreview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                </div>
                                <div className='testSHSATPreview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className="testSHSATPreview__navigatorItem--correct"
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='testSHSATPreview__navigatorItemNumber'>{question.questionNum}</u>
                                                : <p className='testSHSATPreview__navigatorItemNumber'>{question.questionNum}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='testSHSATPreview__navigatorItemCurrent' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </div>
        </>
    );
}

export default TestSHSATPreview;